import React, { useState, useRef, useEffect } from 'react';
import Sidebar from './Sidebar';
import ChatView, { ContactsModal } from './ChatView';
import SideMenu from './SideMenu';
import { loadProjects } from '../services/projectLoader';
import { Send } from 'lucide-react';
import useSEO from '../hooks/useSEO';

const ABOUT_PROJECT = {
  id: 'about',
  name: 'Обо мне',
  username: 'Разработчик ботов',
  color: '#5288c1',
  initials: 'АП',
  isProfile: true,
  tagline: 'Профиль разработчика',
  lastActivity: 'онлайн',
  isOnline: true,
  memberCount: 'онлайн',
  link: null,
  messages: [
    { id: 1, type: 'system', text: 'Добро пожаловать в моё портфолио' },
    { id: 2, type: 'incoming', text: 'Привет! Меня зовут Александр. Я профессиональный разработчик Telegram-ботов с 4-летним коммерческим опытом.', time: '10:00' },
    { id: 3, type: 'incoming', text: 'Специализируюсь на создании бизнес-ботов: от простых информационных до сложных систем с платёжными интеграциями и CRM.', time: '10:01' },
    {
      id: 4, type: 'stats',
      data: [
        { label: 'Проектов', value: '20+' },
        { label: 'Пользователей', value: '85K+' },
        { label: 'Лет опыта', value: '4' },
        { label: 'Клиентов', value: '30+' },
      ],
      time: '10:02',
    },
    {
      id: 5, type: 'tech',
      title: 'Мой технологический стек:',
      items: ['Python 3.11+', 'aiogram 3.x', 'Node.js', 'Telegraf.js', 'PostgreSQL', 'MongoDB', 'Redis', 'Docker', 'Nginx'],
      time: '10:03',
    },
    { id: 6, type: 'incoming', text: 'Готов к новым интересным проектам. Пишите — обсудим детали!', time: '10:04' },
    {
      id: 7, type: 'contact',
      items: [
        { icon: 'send', label: 'Telegram', value: '@extremum7' },
        { icon: 'github', label: 'GitHub', value: 'github.com/extremum7' },
        { icon: 'mail', label: 'Email', value: 'alex@botdev.pro' },
      ],
      time: '10:04',
    },
  ],
};

const UNREAD_CYCLE = [3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const TelegramApp = () => {
  const [projects, setProjects] = useState([ABOUT_PROJECT]);
  const [loading, setLoading] = useState(true);

  const [selectedId, setSelectedId] = useState('about');
  const [displayedId, setDisplayedId] = useState('about');
  const [isTyping, setIsTyping] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadCounts, setUnreadCounts] = useState({ about: 0 });
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [reactions, setReactions] = useState({});
  const [contactsOpen, setContactsOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const typingTimerRef = useRef(null);

  useEffect(() => {
    loadProjects()
      .then(botProjects => {
        const allProjects = [ABOUT_PROJECT, ...botProjects];
        setProjects(allProjects);

        const counts = { about: 0 };
        botProjects.forEach((p, i) => {
          counts[String(p.id)] = UNREAD_CYCLE[i % UNREAD_CYCLE.length];
        });

        // URL hash routing — auto-select project from hash
        const hash = window.location.hash.replace('#', '');
        if (hash && allProjects.find(p => String(p.id) === hash)) {
          setSelectedId(hash);
          setDisplayedId(hash);
          counts[hash] = 0;
          if (window.innerWidth < 768) setMobileShowChat(true);
        }

        setUnreadCounts(counts);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load projects:', err);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const selectedProject = projects.find(p => String(p.id) === String(selectedId));
  const displayedProject = projects.find(p => String(p.id) === String(displayedId));

  const handleSelectProject = (project) => {
    const pid = String(project.id);
    const sid = String(selectedId);

    setUnreadCounts(prev => ({ ...prev, [pid]: 0 }));
    setSelectedId(project.id);

    // Update URL hash for shareable links
    window.history.replaceState(null, '', `#${pid}`);

    if (isMobile) {
      setDisplayedId(project.id);
      setMobileShowChat(true);
      return;
    }

    if (sid === pid) return;

    setIsTyping(true);
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      setDisplayedId(project.id);
      setIsTyping(false);
    }, 1400);
  };

  const handleBackToSidebar = () => setMobileShowChat(false);

  const handleReact = (key, emoji) => {
    setReactions(prev => {
      const msgR = prev[key] || {};
      const count = msgR[emoji] || 0;
      if (count > 0) {
        const { [emoji]: _, ...rest } = msgR;
        if (Object.keys(rest).length === 0) {
          const { [key]: __, ...restAll } = prev;
          return restAll;
        }
        return { ...prev, [key]: rest };
      }
      return { ...prev, [key]: { ...msgR, [emoji]: 1 } };
    });
  };

  const filteredProjects = projects.filter(p =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (p.tagline && p.tagline.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // ── Dynamic SEO: update title, description, JSON-LD per project
  useSEO(selectedProject);

  const showSidebar = !isMobile || !mobileShowChat;
  const showChat = !isMobile || mobileShowChat;

  if (loading) {
    return (
      <div style={{
        display: 'flex', height: '100vh',
        background: '#0e1621',
        alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: '16px',
        fontFamily: "'Inter', -apple-system, sans-serif",
      }}>
        <div style={{
          width: '60px', height: '60px', borderRadius: '50%',
          background: '#5288c1',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          animation: 'tgTypingBounce 1.2s infinite ease-in-out',
        }}>
          <Send size={26} color="#fff" style={{ transform: 'translateX(2px)' }} />
        </div>
        <div style={{ color: '#708499', fontSize: '14px' }}>Загрузка проектов...</div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        overflow: 'hidden',
        background: '#0e1621',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      {/* ContactsModal at top level — visible on ALL screens including mobile sidebar view */}
      <ContactsModal isOpen={contactsOpen} onClose={() => setContactsOpen(false)} />
      <SideMenu
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
        onOpenContacts={() => setContactsOpen(true)}
      />
      {showSidebar && (
        <Sidebar
          projects={filteredProjects}
          selectedId={selectedId}
          onSelectProject={handleSelectProject}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          unreadCounts={unreadCounts}
          isMobile={isMobile}
          onMenuClick={() => setMenuOpen(true)}
        />
      )}
      {showChat && (
        <ChatView
          project={displayedProject}
          headerProject={selectedProject}
          isTyping={isTyping}
          onBack={handleBackToSidebar}
          isMobile={isMobile}
          reactions={reactions}
          onReact={handleReact}
          contactsOpen={contactsOpen}
          setContactsOpen={setContactsOpen}
        />
      )}
    </div>
  );
};

export default TelegramApp;
